import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import { ListWorkPackageDto } from '@codefluegel/zeta-change-typescript-client';
import theme from '../../theme';

import useWorkPackageName from '../../hooks/useWorkPackageName';

const WorkPackageNameCell = (
  params: GridRenderCellParams<ListWorkPackageDto>,
) => {
  const { row } = params;
  const workPackageName = useWorkPackageName(row);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="body2">{workPackageName || 'N/A'}</Typography>
    </Box>
  );
};

type WorkPackageTableProps = {
  workPackages: ListWorkPackageDto[];
  handleWorkPackageModal: (id: number) => void;
};

const commonDataGridStyles = {
  height: 'auto',
  width: '100%',
  '.MuiDataGrid-columnHeaders': {
    backgroundColor: theme.palette.grey[100],
  },
  '.MuiDataGrid-row:last-child': {
    borderBottom: 'none',
  },
  '.MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bold',
  },
  '.MuiDataGrid-cell': {
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  '.MuiDataGrid-row:hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.action.hover,
  },
};

export const useWorkPackageColumns = (): GridColDef<ListWorkPackageDto>[] => {
  const { t } = useTranslation();

  return [
    {
      field: 'workPackageNo',
      headerName: t('workPackage.wpNo'),
      width: 250,
      editable: false,
    },
    {
      field: 'WPNAME',
      headerName: t('workPackage.workPackage'),
      width: 250,
      editable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: WorkPackageNameCell,
    },
    {
      field: 'hours',
      headerName: t('workPackage.hours'),
      width: 80,
      editable: false,
      type: 'number',
    },
    {
      field: 'description',
      headerName: t('workPackage.description'),
      flex: 1,
      minWidth: 200,
      editable: false,
    },
    {
      field: 'status',
      headerName: t('workPackage.implementationStatus'),
      width: 80,
      editable: false,
    },
    {
      field: 'hasAttachments',
      headerName: t('workPackage.attachments'),
      width: 80,
      type: 'boolean',
      editable: false,
    },
  ];
};

const WorkPackageTable = observer(
  ({ workPackages, handleWorkPackageModal }: WorkPackageTableProps) => {
    const workPackageColumns = useWorkPackageColumns();
    return (
      <DataGrid
        rows={workPackages}
        columns={workPackageColumns}
        getRowId={row => row.id}
        hideFooter
        onRowClick={params => handleWorkPackageModal(params.id as number)}
        disableRowSelectionOnClick
        sx={commonDataGridStyles}
      />
    );
  },
);

export default WorkPackageTable;
